import {
  type CreateCrmObjectWidgetData,
  Integration,
  ObjectStateType,
  IWidgetComponentApi,
} from '@playbooks/action-cards-utils'
import type { FunctionComponent } from 'react'

import useHttpService from '../../hooks/useHttpService'
import { useState } from 'react'
import { useTranslation } from '../../contexts/localization/Localization'
import StateManager from '../../services/stateManager'
import { type WidgetProps } from './widgetTypes'
import getDataFromActionCard from '../../utils/getActionCardForWidget'
import replaceWidgetStaticFields from '../../utils/replaceWidgetStaticFields'
import useHandleIntegrationAuth from '../../hooks/useHandleIntegrationAuth'
import useComponentsApiRegistry from '../../hooks/useComponentsApiRegistry'
import { type KeyValueData } from '../../services/httpService'
import { CustomButton } from '../../components/ui/CustomButton'
import { useAppContext } from '../../contexts/AppContext'

interface CreateCrmObjectWidgetProps extends WidgetProps {
  widget: CreateCrmObjectWidgetData
}

/**
 * Widget for various CRM objects creation
 */
const CreateCrmObjectWidget: FunctionComponent<CreateCrmObjectWidgetProps> = ({ widget }) => {
  const { data: state, update: updateState, settings, elementsWithCode } = useAppContext()
  const httpService = useHttpService()
  const { text: t } = useTranslation()

  const [isLoading, setIsLoading] = useState(false)

  const [statusText, setStatusText] = useState('')
  const [crmLink, setCrmLink] = useState('')
  const [forceCreation, setForceCreation] = useState(false)
  const statusConflict = 409

  const { showAuthBtn } = useHandleIntegrationAuth(widget.crmSystem.toLowerCase() as Integration)

  useComponentsApiRegistry(widget.id, () => {
    return {
      activate: handleButtonClick,
    } as IWidgetComponentApi
  })

  async function handleButtonClick() {
    if (!settings?.playId) {
      setStatusText('Context [Play ID] cannot be determined')
      return
    }
    if (isLoading) {
      return
    }
    setIsLoading(true)
    try {
      let data = getDataFromActionCard(state)
      const staticFields = replaceWidgetStaticFields(widget.crmData as KeyValueData, elementsWithCode)
      data = { ...data, ...staticFields }
      const crmResponse = await httpService.createCrmObject(
        widget.crmSystem,
        widget.crmObject,
        settings?.playId,
        data,
        forceCreation,
        showAuthBtn,
      )

      if (!crmResponse) return

      console.log(`CRM service response: ${crmResponse}`)

      setStatusText(t('crmObjectCreationSuccess')?.replace('%crmObject%', widget.crmObject))

      const url = new URL(crmResponse!)

      setCrmLink(url.href)

      const objectState = StateManager.getOrCreate(widget, ObjectStateType.IsCrmObjectCreated)

      // Update the Action Card state (widget value, tags)
      updateState(objectState, 'isActive', true)
    } catch (error) {
      console.error(`Error while creating CRM object: ${error}`)
      const err = error as { status?: number }
      switch (err.status) {
        case statusConflict:
          setStatusText(t('newCrmObjectCreationConfirm'))
          setForceCreation(true)
          break
        default:
          setStatusText(t('crmObjectCreationError')?.replace('%crmObject%', widget.crmObject))
          break
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <CustomButton
        size="lg"
        isLoading={isLoading}
        disabled={!settings?.playId}
        onClick={handleButtonClick}
        tooltip={settings?.playId ? '' : t('playIdNotDetermined')}
      >
        {widget.buttonLabel || t('createCrmObjectWidget_defaultButton')}
      </CustomButton>
      <p>
        {statusText && <span className="text-sm text-gray-500">{statusText}</span>}
        {crmLink && (
          <span className="pl-1">
            <a href={crmLink} target="_blank" className="text-sm text-gray-500">
              {t('navigateToCrmObject')}
            </a>
          </span>
        )}
      </p>
    </div>
  )
}

export default CreateCrmObjectWidget
